import { parseAssist, parseReason, parseSub, extractTeam } from "./helper";
import { TYPE_BY_MT, TYPE_BY_TEXT } from "../constants";

export type TimelineEvt = {
  type: string;
  minute: number;
  extraMinutes?: number;
  team?: "home" | "away";
  player?: string;
  assist?: string;
  raw?: string;
};

export type RenderResult = { title: string; subtitle?: string };
export type Renderer = (ev: TimelineEvt) => RenderResult;

export type TimelineEvent = {
  type: string;
  minute: number;
  extraMinutes?: number;
  team?: "home" | "away";
  player?: string;
  assist?: string;
  raw?: string;
  title?: string;
  subtitle?: string;
  iconKey?: string;
  minuteText?: string;
};

export const mapMtToType = (mt: any): string | undefined => {
  const code = Number(mt);
  return TYPE_BY_MT[code];
};

export const parseTypeFromText = (txt: string): string => {
  const t = String(txt || "").toLowerCase();
  for (const [pattern, type] of TYPE_BY_TEXT) {
    if (pattern.test(t)) return type;
  }
  return "generic";
};

export const extractMinuteParts = (
  n: string
): { minute: number; extra?: number } => {
  const m = n.match(/(\d+)(?:\+(\d+))?'/);
  const minute = m ? Number(m[1]) : 0;
  const extra = m && m[2] ? Number(m[2]) : undefined;
  return { minute, extra };
};

export const parseGoalFromText = (
  n: string
): { scorer?: string; assist?: string } | undefined => {
  const goalRe =
    /goal\s*-\s*([^-()]+?)(?:\s*\(.+?\))?(?:\s*-\s*[^()]*?)?(?:\(\s*assist:\s*([^)]+)\))?/i;
  const mGoal = n.match(goalRe);
  if (!mGoal) return undefined;
  const scorer = (mGoal[1] || "").trim() || undefined;
  const assist = mGoal[2] ? mGoal[2].trim() : undefined;
  return { scorer, assist };
};

export const parseShotScorerFromText = (n: string): string | undefined => {
  const parts = n.split(" - ").map((s) => s.trim());
  for (let i = parts.length - 1; i >= 0; i--) {
    const ptxt = parts[i];
    if (!ptxt) continue;
    const isParenTeam = /^\(.+\)$/.test(ptxt);
    const isDescriptor = /shot\s+(on|off)\s+target/i.test(ptxt);
    if (!isParenTeam && !isDescriptor && !/^\d+'$/.test(ptxt)) {
      return ptxt;
    }
  }
  const rx = /shot\s+(on|off)\s+target.*-\s*([A-Za-zÀ-ž'.\-\s]+)$/i;
  const mShot = n.match(rx);
  if (mShot?.[2]) return mShot[2].trim();
  return undefined;
};

const SCORE_RE = /\b(\d+)\s*-\s*(\d+)\b/; // e.g. 0-0
const sideOf = (t?: "home" | "away") =>
  t ? (t === "home" ? "Home" : "Away") : undefined;
function extractScore(raw?: string): string | undefined {
  if (!raw) return;
  const m = raw.match(SCORE_RE);
  return m ? `${m[1]}-${m[2]}` : undefined;
}

const capitalize = (s?: string) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
/** Extracts labels like "1st Goal", "2nd Corner", "8th Offside", "1st Yellow Card", "2nd Red Card" from raw feed text */
const ordinalLabelFromRaw = (raw?: string): string | undefined => {
  if (!raw) return;
  // Matches: "- 3rd Corner -", "- 1st Goal -", "- 8th Offside -", "- 1st Yellow Card -", "- 2nd Red Card -"
  const m = raw.match(
    /-\s*(\d+(?:st|nd|rd|th)\s+(Goal|Corner|Offside|Yellow(?:\s*Card)?|Red(?:\s*Card)?))\b/i
  );
  return m?.[1] ? capitalize(m[1]) : undefined;
};

export function mergeAssistsIntoGoals(list: TimelineEvent[]): TimelineEvent[] {
  const out: TimelineEvent[] = [];
  for (const ev of list) {
    if (ev.type === "assist" && ev.player) {
      for (let i = out.length - 1; i >= 0; i--) {
        const g = out[i];
        const sameTeam = g.team === ev.team;
        const sameMinute = g.minute === ev.minute;
        const sameExtra =
          (g.extraMinutes ?? undefined) === (ev.extraMinutes ?? undefined);
        if (g.type === "goal" && sameTeam && sameMinute && sameExtra) {
          if (!g.assist) g.assist = ev.player;
          break;
        }
      }
      continue;
    }
    out.push({ ...ev });
  }
  return out;
}

export const LABEL_FOR: Record<string, string> = {
  substitution: "Substitution",
  goal: "Goal",
  assist: "Assist",
  yellow_card: "Yellow Card",
  red_card: "Red Card",
  corner: "Corner",
  offside: "Offside",
  shot_on: "Shot on target",
  shot_off: "Shot off target",
  var_review: "VAR Review",
  penalty_missed: "Missed Penalty",
  race_to_corners: "Race to Corners",
  whistle: "Kick-off / 2nd Half",
  first_half_end: "Half Time",
  full_time: "Full Time",
  period_bucket: "Period",
  extra_time: "Extra Time",
  freekick: "Free Kick",
  goal_kick: "Goal Kick",
  throw_in: "Throw In",
  generic: "",
} as const;

export const RENDERERS: Record<string, Renderer> = {
  substitution: (ev) => {
    const sub = parseSub(ev.raw);
    if (sub) return { title: sub.inPlayer, subtitle: sub.outPlayer };
    return {
      title: ev.player || LABEL_FOR.substitution,
      subtitle: extractTeam(ev.raw),
    };
  },

  goal: (ev) => {
    const ord = ordinalLabelFromRaw(ev.raw); // e.g., "1st Goal"
    const assist = ev.assist ?? parseAssist(ev.raw);
    return {
      // Title prefers ordinal label; fallback to generic label if absent
      title: ord ?? LABEL_FOR.goal,
      // If player name exists, show it; optionally append assist info
      subtitle: ev.player
        ? assist
          ? `${ev.player} (${assist})`
          : ev.player
        : undefined,
    };
  },

  assist: (ev) => ({
    title: ev.player || LABEL_FOR.assist,
  }),

  yellow_card: (ev) => {
    const ord = ordinalLabelFromRaw(ev.raw); // e.g., "1st Yellow Card"
    const reason = parseReason(ev.raw);
    return {
      title: ord ?? LABEL_FOR.yellow_card,
      // Only show something if player exists; otherwise keep empty
      subtitle: ev.player
        ? reason
          ? `${ev.player} — ${reason}`
          : ev.player
        : undefined,
    };
  },

  red_card: (ev) => {
    const ord = ordinalLabelFromRaw(ev.raw); // e.g., "1st Red Card"
    const reason = parseReason(ev.raw);
    return {
      title: ord ?? LABEL_FOR.red_card,
      // Only show something if player exists; otherwise keep empty
      subtitle: ev.player
        ? reason
          ? `${ev.player} — ${reason}`
          : ev.player
        : undefined,
    };
  },

  shot_on: (ev) => ({
    title: LABEL_FOR.shot_on,
    subtitle: ev.player ? ev.player : undefined,
  }),

  shot_off: (ev) => ({
    title: LABEL_FOR.shot_off,
    subtitle: ev.player ? ev.player : undefined,
  }),

  corner: (ev) => {
    const ord = ordinalLabelFromRaw(ev.raw); // e.g., "2nd Corner"
    return {
      // Title prefers ordinal; fallback to generic label
      title: ord ?? LABEL_FOR.corner,
      // If there is no player, don't show team/side; if player exists, show only player
      subtitle: ev.player ? ev.player : undefined,
    };
  },

  offside: (ev) => {
    const ord = ordinalLabelFromRaw(ev.raw); // e.g., "8th Offside"
    return {
      title: ord ?? LABEL_FOR.offside,
      subtitle: ev.player ? ev.player : undefined,
    };
  },

  var_review: (ev) => ({
    title: LABEL_FOR.var_review,
    subtitle: ev.player ? ev.player : undefined,
  }),

  penalty_missed: (ev) => ({
    title: LABEL_FOR.penalty_missed,
    subtitle: ev.player ? ev.player : undefined,
  }),

  race_to_corners: (ev) => {
    const m = ev.raw?.match(/Race to\s+(\d+)\s+Corners/i);
    const count = m?.[1];
    const teamName = extractTeam(ev.raw) || sideOf(ev.team);
    return {
      title: `Race to ${count ?? "N"} Corners`,
      subtitle: teamName,
    };
  },

  whistle: (ev) => ({
    title: LABEL_FOR.whistle,
    subtitle: extractTeam(ev.raw) || ev.raw,
  }),

  first_half_end: (ev) => ({
    title: LABEL_FOR.first_half_end,
    subtitle: (() => {
      const s = extractScore(ev.raw);
      return s ? `Score: ${s}` : ev.raw;
    })(),
  }),

  full_time: (ev) => ({
    title: LABEL_FOR.full_time,
    subtitle: (() => {
      const s = extractScore(ev.raw);
      return s ? `Score: ${s}` : ev.raw;
    })(),
  }),

  period_bucket: (ev) => ({ title: LABEL_FOR.period_bucket, subtitle: ev.raw }),

  extra_time: (ev) => ({
    title: LABEL_FOR.extra_time,
    subtitle: ev.extraMinutes ? `+${ev.extraMinutes}'` : undefined,
  }),

  freekick: (ev) => ({
    title: LABEL_FOR.freekick,
    subtitle: ev.player ? ev.player : undefined,
  }),

  goal_kick: (ev) => ({
    title: LABEL_FOR.goal_kick,
    subtitle: ev.player ? ev.player : undefined,
  }),

  throw_in: (ev) => ({
    title: LABEL_FOR.throw_in,
    subtitle: ev.player ? ev.player : undefined,
  }),

  // default fallback for generic/etc.
  default: (ev) => ({
    title: ev.player || LABEL_FOR.generic,
    subtitle: ev.raw,
  }),
};

export const decorateTimelineEvent = (ev: TimelineEvent): TimelineEvent => {
  const renderer = RENDERERS[ev.type] || RENDERERS.default;
  const { title, subtitle } = renderer(ev);
  return { ...ev, title, subtitle };
};

/** Deduplicate raw feed rows by a stable key */
export function dedupeEvents<
  T extends { mt?: any; tm?: any; ti?: any; n?: any }
>(arr: T[]): T[] {
  const seen = new Set<string>();
  return arr.filter((ev) => {
    const key = `${ev?.mt ?? ""}|${ev?.tm ?? ""}|${ev?.ti ?? ""}|${String(
      ev?.n || ""
    )
      .replace(/\s+/g, " ")
      .trim()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Map a raw feed row to TimelineEvent (+ internal _tm for ordering) */
export const mapRawEventToTimelineEvent = (
  it: any
): TimelineEvent & { _tm: number } => {
  const n: string = it?.n || "";
  const type = mapMtToType(it?.mt) ?? parseTypeFromText(n);
  const { minute, extra } = extractMinuteParts(n);
  const team: "home" | "away" | undefined =
    it?.ti === "1" ? "home" : it?.ti === "2" ? "away" : undefined;
  const player = it?.pl || undefined;
  let scorer = player;
  let assist: string | undefined;

  if (type === "goal") {
    const g = parseGoalFromText(n);
    if (!scorer && g?.scorer) scorer = g.scorer;
    if (g?.assist) assist = g.assist;
  } else if ((type === "shot_on" || type === "shot_off") && !scorer) {
    scorer = parseShotScorerFromText(n) ?? scorer;
  }

  return {
    type,
    minute,
    extraMinutes: extra,
    team,
    player: scorer,
    assist,
    raw: n,
    _tm: Number(it?.tm) || 0,
  };
};

/**
 * Numeric sort value used for ordering timeline around 45' and 90' boundaries.
 * - Treat 45+X as 45.xx so they stay *before* FH divider but after 45'
 * - Treat 90+X as 90.xx so they stay *before* FT divider but after 90'
 * - FH/FT rows themselves are placed at 45.99 / 90.99
 */
export const sortScore = (e: TimelineEvent & { _tm: number }): number => {
  const minute = Number(e.minute) || 0;
  const extra = Number(e.extraMinutes) || 0;

  if (e.type === "first_half_end") return 45.99;
  if (e.type === "full_time") return 90.99;

  if ((minute === 45 || minute === 90) && extra > 0) {
    // Cap fraction to keep anything under the divider but after the base minute
    return minute + Math.min(0.98, extra / 100);
  }

  return minute;
};
